'use client';

import { useCallback } from 'react';
import { TransactionSignature } from '@solana/web3.js';
import { VaultClient, VaultDepositor } from '@drift-labs/vaults-sdk';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';
import { useWaitForTxConfirm } from '../transactions/useWaitForTxConfirm';
import { mapTxParamsToVaultTxParams } from './utils';

export const useWithdrawFromVault = () => {
	const getTxParams = useGetTxParams();
	const { waitForTxConfirm } = useWaitForTxConfirm();

	const withdrawFromVault = useCallback(
		async (
			vaultDepositor: VaultDepositor,
			vaultClient: VaultClient
		): Promise<TransactionSignature | undefined> => {
			if (!vaultClient || !vaultDepositor) {
				console.error('No vault client or vault depositor provided');
				return undefined;
			}

			const toastId = 'withdrawFromVault';

			try {
				notify({
					type: 'awaiting',
					message: 'Awaiting transaction confirmation',
					id: toastId,
					showUntilCancelled: true,
				});

				const txSig = await vaultClient.withdraw(
					vaultDepositor.pubkey,
					mapTxParamsToVaultTxParams(getTxParams())
				);

				await waitForTxConfirm(txSig);

				notify({
					type: 'success',
					message: 'Withdrawal successful.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return txSig;
			} catch (err: any) {
				console.error(err);
				notify({
					type: 'error',
					message: 'There was an error withdrawing from this vault.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});
				return undefined;
			}
		},
		[getTxParams, waitForTxConfirm]
	);

	return { withdrawFromVault };
};
